/**
 * WebSocket с автопереподключением.
 *
 * Соединение рвётся постоянно и это норма: телефон уснул, сеть переключилась
 * с Wi-Fi на мобильную, оператор подрезал долгие соединения. Поэтому здесь
 * нет «ошибки подключения» как исключительной ситуации — есть цикл, который
 * поднимает связь заново, и обработчик onReconnect, по которому приложение
 * догоняет пропущенное.
 */
import type { ServerEvent } from './types.js';

type State = 'connecting' | 'online' | 'offline';

const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30_000;
/** Свой ping поверх протокольного: часть промежуточных узлов рвёт «молчащие» соединения. */
const PING_INTERVAL_MS = 25_000;

export class Socket {
  private ws: WebSocket | null = null;
  private token: string | null = null;
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private closedByUs = false;

  onEvent: (event: ServerEvent) => void = () => {};
  onStateChange: (state: State) => void = () => {};
  /** Вызывается после каждой успешной авторизации — момент, когда нужно синхронизироваться. */
  onReconnect: () => void = () => {};

  constructor() {
    // Браузер сообщает о возврате сети и о показе вкладки — оба повода
    // не ждать очередного шага экспоненциальной задержки, а пробовать сразу.
    addEventListener('online', () => this.reconnectNow());
    addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.reconnectNow();
    });
  }

  connect(token: string): void {
    this.token = token;
    this.closedByUs = false;
    this.open();
  }

  private open(): void {
    if (!this.token) return;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.onStateChange('connecting');

    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${protocol}//${location.host}/ws`);
    this.ws = ws;

    ws.addEventListener('open', () => {
      // Токен уходит отдельным кадром, а не в URL: адрес попадает в логи
      // прокси и в историю, а задать заголовок для WebSocket из браузера нельзя.
      ws.send(JSON.stringify({ type: 'auth', token: this.token }));
    });

    ws.addEventListener('message', (event) => {
      let parsed: ServerEvent;
      try {
        parsed = JSON.parse(event.data as string) as ServerEvent;
      } catch {
        return;
      }

      if (parsed.type === 'ready') {
        this.attempt = 0;
        this.onStateChange('online');
        this.startPing();
        this.onReconnect();
        return;
      }
      this.onEvent(parsed);
    });

    ws.addEventListener('close', () => {
      this.stopPing();
      this.onStateChange('offline');
      if (!this.closedByUs) this.scheduleReconnect();
    });

    ws.addEventListener('error', () => ws.close());
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;

    // Экспоненциальная задержка с разбросом: без разброса все клиенты
    // ломятся обратно одновременно после общего сбоя сети.
    const delay = Math.min(BASE_DELAY_MS * 2 ** this.attempt, MAX_DELAY_MS);
    const jitter = delay * 0.3 * Math.random();
    this.attempt++;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, delay + jitter);
  }

  private reconnectNow(): void {
    if (this.closedByUs || !this.token) return;
    if (this.ws?.readyState === WebSocket.OPEN) return;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.attempt = 0;
    this.open();
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  get isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /** Возвращает false, если отправить не вышло — вызывающий уходит на HTTP. */
  send(payload: unknown): boolean {
    if (!this.isOpen) return false;
    this.ws!.send(JSON.stringify(payload));
    return true;
  }

  close(): void {
    this.closedByUs = true;
    this.token = null;
    this.stopPing();
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.ws?.close();
    this.ws = null;
  }
}
